import { createCompletionServiceSelector } from '../../../features/tracking/services/CompletionService';
import { StudentThunkDispatch } from '../../../types/redux';
import { Guid } from '../../model/guid';
import { StudentStore } from '../../models/Store';
import { ItemsReducerState } from '../../reducers/items/itemsReducer';
import { GetItemById, getItemByIdSelector } from '../../reducers/items/itemsSelectors';
import { trackingCompleted } from '../../reducers/student/tracking/completionTrackingActions';
import { CourseCompletionStatus, IPersistenceTrackingClient, QuizStatus, TrackingData } from './clients/trackingClient';
import { getTrackingClient } from './clients/trackingClientFactory';
import { getChildrenForScormCreator } from './selectors/getChildrenForScormCreator';
import { getParentCreator } from './selectors/getParentCreator';
import { ITrackingCompletionService, TrackingCompletionService } from './trackingCompletionService';
import { TrackingPersistenceMiddlewareHandler } from './trackingPersistenceMiddlewareHandler';
import { convertItemIdToTrackingIdCreator } from './utils/itemIdToTrackingIdConverter';
import { ensureNonSequencedFlowItem, HotspotTab, isNonSequencedFlowItem, Item } from '@ael/core-api';
import { Action, MiddlewareAPI } from '@reduxjs/toolkit';
import { List } from 'immutable';

export const getAllHotspotTabs = (items: ItemsReducerState, getItemById: GetItemById): List<HotspotTab> => {
  const hotspotIds = items
    .filter((item: Item) => isNonSequencedFlowItem(item))
    .map((item: Item) => {
      ensureNonSequencedFlowItem(item);
      return item.hotspots;
    })
    .flatten() as List<string>;
  const hotspots = hotspotIds.map((hotspotId: string) => getItemById(hotspotId) as HotspotTab);
  return hotspots;
};

export const getHotspotTab = (tabId: Guid, items: ItemsReducerState, getItemById: GetItemById): HotspotTab | null => {
  const tabs = getAllHotspotTabs(items, getItemById);
  const tab = tabs.find((tab: HotspotTab) => tab.id === tabId);
  return tab ?? null;
};

export const prepareTrackingPersistenceMiddleware =
  (persistenceTrackingClient: IPersistenceTrackingClient) =>
  ({ getState, dispatch }: MiddlewareAPI<StudentThunkDispatch, StudentStore>) => {
    const client = getTrackingClient(
      getParentCreator(() => getState().items),
      getChildrenForScormCreator(() => getState().items),
      convertItemIdToTrackingIdCreator(() => getState().items),
      persistenceTrackingClient
    );

    const markCompleted = () => dispatch(trackingCompleted());
    return (next: StudentThunkDispatch) =>
      (action: Action): any => {
        const previousState = getState();
        next(action);
        const newState = getState();

        const createTrackingCompletionService = (): ITrackingCompletionService => {
          const completionService = createCompletionServiceSelector(newState);
          const getItemById = getItemByIdSelector(newState);
          return new TrackingCompletionService(getItemById, completionService);
        };

        const updatePersistedData = async (trackingData: TrackingData) => await client.setTrackingData(trackingData);

        const setCompletionsStatus = async (completionStatus: CourseCompletionStatus) => {
          if (completionStatus === CourseCompletionStatus.Completed) {
            markCompleted();
          }
          await client.setCompletionStatus(completionStatus);
        };

        const setQuizStatus = async (quizStatus: QuizStatus) => {
          await client.setQuizStatus(quizStatus);
        };

        const handler = new TrackingPersistenceMiddlewareHandler(
          createTrackingCompletionService,
          updatePersistedData,
          setCompletionsStatus,
          setQuizStatus
        );

        handler.handle({
          previousFinishedItems: previousState.tracking.items.finishedItems,
          previousQuestionWidgetsState: previousState.tracking.questionWidget.previouslySelectedOptionsForItems,
          newFinishedItems: newState.tracking.items.finishedItems,
          newQuestionWidgetsState: newState.tracking.questionWidget.previouslySelectedOptionsForItems,
          trackedItems: newState.tracking.items.trackedItems,
          previousTrackedQuizState: previousState.tracking.trackedQuiz,
          newTrackedQuizState: newState.tracking.trackedQuiz,
        });
      };
  };
